import React, { useState, useRef, ChangeEvent } from 'react';
import { Box, Stack, Typography, CircularProgress, Skeleton } from '@mui/material';
import pdfDownload from '@assets/icons/pdf-download.svg';
import uploadIcon from '@assets/icons/upload-icon.svg';
import downloadIcon from '@assets/icons/download_icon_green.svg';
import { CustomTooltip } from '@components';
import { defaultTooltipStyle } from '@utils/styles';
import useUploadNda from '@hooks/api/useUploadNDA';
import { useLazyFetchSelfServeAtachmentsQuery } from '@api/selfServe';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';

// Define the props interface for the component
interface GameDetailsCardProps {
  gameIcon: string | null;
  gameName: string | null;
  gameId: string | null;
  email: string | null;
  companyName: string | null;
  category: string | null;
  genre: string | null;
  appStoreUrl: string | null;
  playStoreUrl: string | null;
  attachmentId: string | null;
  selfServeId: string | null; // Add selfServeId prop for the upload
  onNdaUploaded?: () => void; // Callback for when NDA is uploaded successfully
  isLoading?: boolean; // Add loading prop
}

const GameDetailsCard: React.FC<GameDetailsCardProps> = ({
  gameIcon,
  gameName,
  gameId,
  email,
  companyName,
  category,
  genre,
  appStoreUrl,
  playStoreUrl,
  attachmentId,
  selfServeId,
  onNdaUploaded,
  isLoading = false,
}) => {
  const dispatch = useDispatch();

  const { uploadNda, isLoading: uploadNdaLoading } = useUploadNda();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [fetchSelfServeAtachments] = useLazyFetchSelfServeAtachmentsQuery();

  const handleDownloadAttachment = async () => {
    if (!selfServeId || !attachmentId) return;

    let downloadLink = '';

    try {
      const response: any = await fetchSelfServeAtachments({
        selfServeId,
        attachmentId,
      }).unwrap();
      downloadLink = response?.data?.url || '';
      if (!downloadLink) {
        return;
      }

      const link = document.createElement('a');
      link.href = downloadLink;
      link.target = '_blank';
      link.download = `PvX Partners - ${companyName} - NDA.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      dispatch(
        setToastMessage({
          heading: 'Download Failed',
          message: 'Unable to download attachment. Please try again later.',
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      // Auto upload the file when selected
      handleUploadNda(event.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    if (uploadNdaLoading) return;

    // Trigger the file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadNda = async (file: File) => {
    if (!selfServeId) {
      console.error('Missing selfServeId for NDA upload');
      return;
    }

    const result = await uploadNda(selfServeId, file);

    if (result.success) {
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);

      // Call the callback function if provided
      if (onNdaUploaded) {
        onNdaUploaded();
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#FFFFFF',
          padding: '1rem',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '0.75rem',
          }}
        >
          <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: '4px' }} />
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={150} height={20} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              minWidth: '50%',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginRight: '0.5rem',
            }}
          >
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={120} height={24} />
          </Box>
          <Box
            sx={{
              minWidth: '50%',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              maxWidth: '8rem',
            }}
          >
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={120} height={24} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              minWidth: '50%',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginRight: '0.5rem',
              maxWidth: '8rem',
            }}
          >
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={120} height={24} />
          </Box>
          <Box
            sx={{
              minWidth: '50%',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              maxWidth: '8rem',
            }}
          >
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={120} height={24} />
          </Box>
        </Box>

        <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
          <Skeleton variant="text" width={120} height={24} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        padding: '1rem',
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '0.75rem',
        }}
      >
        <img
          src={gameIcon || ''}
          alt="icon"
          style={{
            width: '4rem',
            height: '4rem',
            borderRadius: '4px',
          }}
        />
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              lineHeight: '2.5rem',
            }}
          >
            {gameName}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: '#6C6C6C',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            {[companyName, category, genre].filter(Boolean).join(' • ')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            minWidth: '50%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginRight: '0.5rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: '#6C6C6C',
            }}
          >
            Game ID
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              color: '#162936',
            }}
          >
            {gameId}
          </Typography>
        </Box>
        <Box
          sx={{
            minWidth: '50%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            maxWidth: '8rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: '#6C6C6C',
            }}
          >
            Email ID
          </Typography>
          <CustomTooltip
            customStyle={{
              '& .MuiTooltip-tooltip': { ...defaultTooltipStyle },
            }}
            content={email || ''}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#162936',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
            >
              {email}
            </Typography>
          </CustomTooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            minWidth: '50%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginRight: '0.5rem',
            maxWidth: '8rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: '#6C6C6C',
            }}
          >
            Appstore Link
          </Typography>
          {appStoreUrl ? (
            <CustomTooltip
              customStyle={{
                '& .MuiTooltip-tooltip': { ...defaultTooltipStyle },
              }}
              content={appStoreUrl}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#162936',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ':hover': {
                    cursor: 'pointer',
                  },
                }}
                onClick={() => openInNewTab(appStoreUrl)}
              >
                {appStoreUrl}
              </Typography>
            </CustomTooltip>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#162936',
              }}
            >
              -
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            minWidth: '50%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            maxWidth: '8rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: '#6C6C6C',
            }}
          >
            Playstore Link
          </Typography>
          {playStoreUrl ? (
            <CustomTooltip
              customStyle={{
                '& .MuiTooltip-tooltip': { ...defaultTooltipStyle },
              }}
              content={playStoreUrl}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#162936',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ':hover': {
                    cursor: 'pointer',
                  },
                }}
                onClick={() => openInNewTab(playStoreUrl)}
              >
                {playStoreUrl}
              </Typography>
            </CustomTooltip>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#162936',
              }}
            >
              -
            </Typography>
          )}
        </Box>
      </Box>

      {attachmentId ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: '0.5rem',
            textAlign: 'left',
          }}
        >
          <img
            src={pdfDownload}
            alt="action_icon"
            style={{
              userSelect: 'none',
              marginRight: '0.2rem',
              width: '1rem',
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#162936',
              marginRight: '0.2rem',
            }}
          >
            NDA Document
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              handleDownloadAttachment();
              e.stopPropagation();
            }}
          >
            <img src={downloadIcon} alt="upload-icon" style={{ userSelect: 'none' }} />
          </Box>
        </Box>
      ) : (
        <></>
      )}

      <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={handleUploadClick}
        >
          {uploadNdaLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <img src={uploadIcon} alt="upload-icon" style={{ userSelect: 'none' }} />
          )}
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#51B8B0',
              marginRight: '0.2rem',
            }}
          >
            {uploadNdaLoading ? 'Uploading...' : attachmentId ? 'Re-Upload NDA' : 'Upload NDA'}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default GameDetailsCard;
